import React, { useEffect, useState, useRef } from "react";
import { message } from "antd";
import { createContainer } from "unstated-next";
import request from "project-config/services/request/request";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { useSubAccountStore } from "@/store/SubAccountStore";
import { SyncStatusEnum } from "../components/child-integration-setting/constant";

export interface IdeptInfo {
  parentId: number | null;
  deptId: number | null;
  name: string;
  isSync?: number;
  id?: number | null;
  totalPersonnel?: number;
  position?: string | null;
  isLeader?: number | null;
  isExist?: number | null;
  deptList: IdeptInfo[];
  keyIndex: number;
}

export function useDingTalkSubAccountHook() {
  const t = getTranslateNsT("childManager");
  const [openSettingModal, setOpenSettingModal] = useState(false);
  const [checkTreeKeys, setCheckTreeKeys] = useState<React.Key[]>([]);
  const [checkTreeInfo, setCheckTreeInfo] = useState<IdeptInfo[]>([]);
  const [currentEnterpriseAlias, setCurrentEnterpriseAlias] = useState("");
  const [expandeDeptyKeys, setExpandedDeptyKeys] = useState<React.Key[]>([]);
  const [isInitPage, setIsInitPage] = useState(false);
  const [currentBtnStatus, setCurrentBtnStatus] = useState(false);
  const [initPageLoading, setInitPageLoading] = useState(false);
  const [deptInfo, setDeptInfo] = useState<IdeptInfo[]>([]);
  const [initDeptInfo, setInitDeptInfo] = useState<IdeptInfo[]>([]);
  const { fetchDingTalkSubAccountList } = useSubAccountStore();
  const [isAutoExpandParent, setIsAutoExpandParent] = useState(true);
  const [isCheckKeyData, setIsCheckKeyData] = useState(false);
  const [credentialsInfo, setCredentialsInfo] = useState({});
  const [isSyncData, setIsSyncData] = useState(false);
  const [isSyncDataFail, setIsSyncDataFail] = useState(false);
  const [isSearchExpandKey, setIsSearchExpandKey] = useState(false);
  const [selectTreeKey, setSelectTreeKey] = useState<React.Key>();
  let syncTimer = useRef<NodeJS.Timer | null>(null);

  const queryDingTalkAppSettings = async () => {
    setInitPageLoading(true);
    const { code, data } = await request.post(
      "/api/rendering/user/queryDingTalkAppSettings"
    );
    if (code !== 200) return;
    // 查询当前同步状态（0-未开始 1-同步中 2-同步成功 3-同步失败）
    const { data: syncData } = await request.post(
      "/api/rendering/user/getDingTalkDataSyncStatus",
      {}
    );
    setInitPageLoading(false);
    setIsInitPage(!data.isImport);
    if (data.isImport) {
      setCurrentEnterpriseAlias(data.enterpriseAlias);
      queryDeptList();
      return;
    }
    if (!data.appKey && !data.appSecret) return;
    // 表示当前在同步中，初始页+loading状态
    if (data.appKey && data.appSecret) {
      setIsCheckKeyData(true);
      setCredentialsInfo(data);
    }
    if (syncData.isSync === SyncStatusEnum.FAIL) {
      setIsSyncDataFail(true);
    }
    if (syncData.isSync === SyncStatusEnum.SYNCING) {
      setIsSyncData(true);
      setIsSyncDataFail(false);
      if (syncTimer.current) return;
      syncTimer.current = setInterval(() => {
        checkSyncStatus();
      }, 5000);
    }
  };
  // 轮询当前同步状态
  const checkSyncStatus = async () => {
    const { data, code } = await request.post(
      "/api/rendering/user/getDingTalkDataSyncStatus",
      {}
    );
    if (code !== 200) return;
    if (!syncTimer.current) return;
    if ([SyncStatusEnum.FAIL, SyncStatusEnum.SUCCESS].includes(data.isSync)) {
      window.clearInterval(syncTimer.current);
      syncTimer.current = null;
      setIsSyncData(false);
    }
    if (data.isSync === SyncStatusEnum.FAIL) {
      setIsSyncDataFail(true);
    }
    if (data.isSync === SyncStatusEnum.SUCCESS) {
      queryDeptList();
      setIsInitPage(false);
      message.success(t("dingtalk_subaccount_setting_synchronous_success"));
    }
  };

  const queryDeptList = async () => {
    const { data } = await request.post<IdeptInfo[]>(
      "/api/rendering/user/queryDeptList"
    );
    const result = data ?? [];
    setDeptInfo(result);
    setInitDeptInfo(result);
    const defaultDepty =
      data && data.length && data[0].deptList && data[0].deptList.length
        ? data[0].deptList[0].deptId
        : undefined;
    const defaultKeyIndex =
      data && data.length && data[0].deptList && data[0].deptList.length
        ? data[0].deptList[0].keyIndex
        : undefined;
    setSelectTreeKey(defaultKeyIndex);
    setExpandedDeptyKeys(defaultKeyIndex ? [defaultKeyIndex] : []);
    fetchDingTalkSubAccountList({ deptId: defaultDepty || undefined });
  };

  const handleSynchronousData = async () => {
    const { code } = await request.post(
      "/api/rendering/user/syncDingTalkData",
      {}
    );
    if (code === 200) {
      setIsSyncData(true);
      setIsSyncDataFail(false);
      syncTimer.current = setInterval(() => {
        checkSyncStatus();
      }, 5000);
    }
  };

  useEffect(() => {
    queryDingTalkAppSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    syncTimer,
    openSettingModal,
    setOpenSettingModal,
    checkTreeKeys,
    setCheckTreeKeys,
    isInitPage,
    currentBtnStatus,
    setCurrentBtnStatus,
    currentEnterpriseAlias,
    expandeDeptyKeys,
    setExpandedDeptyKeys,
    initPageLoading,
    queryDeptList,
    deptInfo,
    queryDingTalkAppSettings,
    checkTreeInfo,
    setCheckTreeInfo,
    setDeptInfo,
    setInitDeptInfo,
    initDeptInfo,
    isAutoExpandParent,
    setIsAutoExpandParent,
    isCheckKeyData,
    credentialsInfo,
    isSyncData,
    setIsSyncData,
    isSyncDataFail,
    handleSynchronousData,
    setIsSyncDataFail,
    isSearchExpandKey,
    setIsSearchExpandKey,
    selectTreeKey,
    setSelectTreeKey,
  };
}

const DingTalkSubAccountContainer = createContainer(useDingTalkSubAccountHook);

export const DingTalkSubAccountProvider = DingTalkSubAccountContainer.Provider;

export function useDingTalkSubAccountStore() {
  return DingTalkSubAccountContainer.useContainer();
}
